/**
 * motion.ts — Núcleo puro del sistema de animación de gráficos y KPIs.
 *
 * QUÉ HACE: concentra duraciones, easings, interpolación numérica y un
 * programador de `requestAnimationFrame` COMPARTIDO (un solo bucle para todas
 * las animaciones activas, clave para no saturar móvil/tablet).
 * QUÉ INVOCA: nada (sin React, sin Recharts, sin datos de negocio).
 * QUÉ EXPORTA: MOTION, easings, lerp, roundTo, staggeredProgress,
 * subscribeFrame, staggerDelay y barStaggerDelay.
 * QUÉ NO DEBE HACER: no conoce componentes, dominio ni persistencia.
 */

/** Duraciones (ms) y easings únicos del sistema de animación. */
export const MOTION = {
  /** Duración de la transición de datos (subida/bajada de series y conteo). */
  series: 600,
  /** Duración del pop de entrada de tarjetas KPI y celdas. */
  enter: 380,
  /** Duración del trazo de líneas y áreas (stroke-dashoffset). */
  draw: 600,
  /** Duración del destello de cambio de estado en una tarjeta KPI. */
  flash: 750,
  /**
   * Duración de la subida de UNA barra. El avance es **secuencial**: cada barra
   * espera a que la anterior termine (`barStaggerDelay` = índice × barRise).
   */
  barRise: 110,
  /** Retardo por elemento para entradas escalonadas (celdas, filas). */
  staggerStep: 18,
  /** Tope de retardo escalonado acumulado (evita colas largas). */
  staggerMax: 260,
  /** Tope del retardo por barra (series muy largas: 24 h, 31 días). */
  barStaggerMax: 6000,
} as const;

/** Easing estándar de salida (rápido al inicio, suave al final). */
export const EASE_OUT_CUBIC = (t: number): number => 1 - (1 - t) ** 3;

/** Limita un valor al rango [0, 1]. */
export const clamp01 = (value: number): number =>
  value < 0 ? 0 : value > 1 ? 1 : value;

/** Interpolación lineal entre `from` y `to`. */
export const lerp = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress;

/** Redondea a una cantidad fija de decimales (para conteos de KPI). */
export const roundTo = (value: number, decimals: number): number => {
  if (decimals <= 0) return Math.round(value);
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/**
 * Progreso (0–1, ya suavizado) de un elemento `index` dentro de una animación
 * escalonada: cada elemento arranca `index * stagger` ms después. Con
 * `stagger === duration` el avance es secuencial (uno termina y empieza el otro).
 */
export const staggeredProgress = (
  elapsed: number,
  index: number,
  duration: number,
  stagger: number,
): number => {
  const delay = index * stagger;
  return EASE_OUT_CUBIC(clamp01((elapsed - delay) / duration));
};

/** Callback del bucle compartido de animación. */
type FrameCallback = (now: number) => void;

const frameCallbacks = new Set<FrameCallback>();
let frameId = 0;

const runFrame = (now: number): void => {
  frameId = 0;
  for (const callback of Array.from(frameCallbacks)) callback(now);
  if (frameCallbacks.size > 0) frameId = requestAnimationFrame(runFrame);
};

/**
 * Suscribe un callback al bucle compartido de `requestAnimationFrame`.
 * Devuelve la función para cancelar la suscripción (idempotente).
 */
export const subscribeFrame = (callback: FrameCallback): (() => void) => {
  if (typeof requestAnimationFrame !== "function") return () => undefined;
  frameCallbacks.add(callback);
  if (frameId === 0) frameId = requestAnimationFrame(runFrame);
  return () => {
    frameCallbacks.delete(callback);
    if (frameCallbacks.size === 0 && frameId !== 0) {
      cancelAnimationFrame(frameId);
      frameId = 0;
    }
  };
};

/** Convierte un valor CSS de retardo escalonado a string seguro. */
export const staggerDelay = (
  index: number,
  step: number = MOTION.staggerStep,
): string => `${Math.min(index * step, MOTION.staggerMax)}ms`;

/**
 * Retardo por barra en **orden de lectura secuencial**: cada barra arranca
 * cuando la anterior termina (índice × `barRise`). Lunes→Domingo, 12AM→11PM,
 * semanas, días…
 */
export const barStaggerDelay = (
  index: number,
  step: number = MOTION.barRise,
): string => `${Math.min(index * step, MOTION.barStaggerMax)}ms`;
